import { useEffect, useRef, type ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { Building2, CalendarDays, ChevronLeft, MessageCircle, Sun, Users, UsersRound } from 'lucide-react'
import { useEnv } from '../context'
import { useClubContext } from '../data/hooks'
import type { HubContext } from '../platform/platform'
import { NetworkStatus } from './NetworkStatus'

const NAV = [
  { to: '/today', label: 'Сегодня', icon: Sun },
  { to: '/schedule', label: 'Расписание', icon: CalendarDays },
  { to: '/athletes', label: 'Спортсмены', icon: Users },
  { to: '/groups', label: 'Группы', icon: UsersRound },
  { to: '/club', label: 'Клуб', icon: Building2 },
]

export function BottomNav() {
  return (
    <nav className="pc-nav" aria-label="Разделы">
      <ul>
        {NAV.map(({ to, label, icon: Icon }) => (
          <li key={to}>
            <NavLink to={to}>
              <Icon size={22} aria-hidden="true" />
              {label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}

/** Кнопка Штаба: в мини-приложениях ведёт в бота, в PWA - в свой чат. */
export function HubButton({ context, inline }: { context?: HubContext; inline?: boolean }) {
  const { platform, toast } = useEnv()
  const navigate = useNavigate()
  const label = platform.hubMode === 'bot' ? 'Спросить Штаб в боте' : 'Спросить Штаб'

  const open = () => {
    // В мини-приложении с контекстом экрана - сначала «Обсудить в боте» (S16):
    // там видно, что уйдёт в бота, и готовится одноразовый код
    if (platform.hubMode === 'bot' && context?.ref) {
      navigate('/hub/bot', { state: { context } })
      return
    }
    if (platform.openHub(context)) {
      toast('Открываю бота. Ответ придёт туда же, где вы спросили.')
      return
    }
    // Бот не открылся - экран S16 объяснит и покажет запасной путь
    if (platform.hubMode === 'bot') {
      navigate('/hub/bot')
      return
    }
    navigate('/hub', { state: { context } })
  }

  if (inline) {
    return (
      <button type="button" className="pc-btn pc-btn--block pc-btn--row" onClick={open}>
        {label}
        <MessageCircle size={20} aria-hidden="true" />
      </button>
    )
  }
  return (
    <button type="button" className="pc-hub-button" onClick={open} aria-label={label}>
      <MessageCircle size={18} aria-hidden="true" />
      Штаб
    </button>
  )
}

/**
 * Каркас экрана: шапка с клубом и направлением, заголовок, Штаб справа.
 * back - путь назад; в мини-приложениях дублируется системной кнопкой.
 */
export function Screen({
  title,
  subtitle,
  back,
  hubContext,
  hideHub,
  headerAction,
  actionBar,
  hideNav,
  layout,
  children,
}: {
  title: string
  subtitle?: ReactNode
  back?: () => void
  hubContext?: HubContext
  /** На экране самого Штаба кнопка в шапке вела бы на тот же экран. */
  hideHub?: boolean
  /** Кнопка в шапке слева от Штаба: меню действий экрана. */
  headerAction?: ReactNode
  /** Главное действие, закреплённое над нижним меню. */
  actionBar?: ReactNode
  /** Мастер первого запуска: разделов ещё нет, меню не показываем. */
  hideNav?: boolean
  /** chat - лента на всю высоту и поле ввода, прижатое к низу (Штаб в PWA). */
  layout?: 'chat'
  children: ReactNode
}) {
  const { platform } = useEnv()
  const club = useClubContext()
  const headingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    if (!back) return
    return platform.useBackButton(back)
  }, [back, platform])

  // Переход между экранами переносит фокус на заголовок: экранный диктор
  // объявляет новый экран, а не молчит.
  useEffect(() => {
    headingRef.current?.focus()
  }, [title])

  // Закреплённая панель действий не закрывает поле в фокусе: форма
  // подкручивается так, чтобы поле (с подсказкой и ошибкой) было над панелью.
  const hasActionBar = !!actionBar
  useEffect(() => {
    if (!hasActionBar) return
    let timer: ReturnType<typeof setTimeout> | undefined
    const onFocus = (ev: FocusEvent) => {
      const el = ev.target
      if (!(el instanceof HTMLElement) || !el.matches('input, textarea, select') || el.closest('[role="dialog"]')) return
      clearTimeout(timer)
      // Клавиатура на телефоне открывается не мгновенно: меряем после неё.
      timer = setTimeout(() => keepAboveActionBar(el), 250)
    }
    document.addEventListener('focusin', onFocus)
    return () => {
      document.removeEventListener('focusin', onFocus)
      clearTimeout(timer)
    }
  }, [hasActionBar])

  const crumbs = club.data ? `${club.data.clubName} / ${club.data.directionName}` : ' '

  return (
    <div className="pc-app">
      <main
        className={['pc-main', actionBar && 'pc-main--with-action', layout === 'chat' && 'pc-main--chat']
          .filter(Boolean)
          .join(' ')}
      >
        <div className="pc-topbar">
          <span className="pc-crumbs">{crumbs}</span>
          <NetworkStatus />
        </div>
        <div className="pc-title-row">
          {back && (
            <button type="button" className="pc-back" onClick={back} aria-label="Назад">
              <ChevronLeft size={26} aria-hidden="true" />
            </button>
          )}
          <h1 className="pc-title" tabIndex={-1} ref={headingRef}>
            {title}
          </h1>
          {headerAction}
          {!hideHub && <HubButton context={hubContext} />}
        </div>
        {subtitle && <p className="pc-subtitle">{subtitle}</p>}
        {children}
      </main>
      {actionBar && (
        <div className="pc-action-bar">
          <div>{actionBar}</div>
        </div>
      )}
      {!hideNav && <BottomNav />}
    </div>
  )
}

/** Если закреплённая панель действий перекрывает поле, страница прокручивается на высоту перекрытия. */
export function keepAboveActionBar(el: HTMLElement) {
  const bar = document.querySelector('.pc-action-bar')
  if (!bar) return
  const box = (el.closest('.pc-field') ?? el).getBoundingClientRect()
  const overlap = box.bottom + 12 - bar.getBoundingClientRect().top
  // Не дальше, чем верх поля остаётся виден.
  if (overlap > 0) window.scrollBy({ top: Math.max(0, Math.min(overlap, box.top - 8)) })
}
